<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";

import Button from "primevue/button";
import Card from "primevue/card";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import { useAppToast } from "@/shared/lib/useAppToast";
import { usePagination } from '@/shared/composables/usePagination'

import DeleteProductConfirmDialog from "@/modules/admin/products/products/components/DeleteProductConfirmDialog.vue";
import ProductFormDialog from "@/modules/admin/products/products/components/ProductFormDialog.vue";

import {
  createProduct,
  updateProduct,
  deleteProduct as deleteProductApi,
} from "@/modules/admin/products/products/api/products.service";

import { getAllProductTypes } from "@/modules/admin/products/product-types/api/productTypes.service";
import { getResolvedProducts } from "@/modules/catalog/api/catalog.service";

const router = useRouter();
const toast = useAppToast();

const products = ref([]);
const productTypes = ref([]);
const loading = ref(false);

const search = ref("");
const selectedBrandId = ref(null);
const selectedCategoryId = ref(null);

const formVisible = ref(false);
const formMode = ref("create");
const selectedProduct = ref(null);

const deleteDialogVisible = ref(false);
const productToDelete = ref(null);

const submitting = ref(false);
const deleting = ref(false);

async function loadProducts() {
  loading.value = true;

  try {
    const data = await getResolvedProducts();
    products.value = data.map((p) => ({
      ...p,
      defaultImageUrl:
        p.defaultImageUrl ||
        p.images?.find((img) => img.isDefault)?.url ||
        null,
    }));
  } catch (error) {
    products.value = [];
    toast.error(error.message || "Ошибка при загрузке товаров");
  } finally {
    loading.value = false;
  }
}

async function loadProductTypes() {
  try {
    productTypes.value = await getAllProductTypes();
  } catch (error) {
    productTypes.value = [];
    toast.error(error.message || "Ошибка при загрузке типов товаров");
  }
}

const brandOptions = computed(() => {
  const map = new Map();
  products.value.forEach((p) => p.brandId && map.set(p.brandId, p.brandName));

  return [
    { label: "Все бренды", value: null },
    ...Array.from(map.entries()).map(([id, name]) => ({
      label: name,
      value: id,
    })),
  ];
});

const categoryOptions = computed(() => {
  const map = new Map();
  products.value.forEach(
    (p) => p.categoryId && map.set(p.categoryId, p.categoryName),
  );

  return [
    { label: "Все категории", value: null },
    ...Array.from(map.entries()).map(([id, name]) => ({
      label: name,
      value: id,
    })),
  ];
});

const filteredProducts = computed(() =>
  products.value.filter((product) => {
    const searchText = search.value.trim().toLowerCase();

    const matchesSearch =
      !searchText ||
      product.name?.toLowerCase().includes(searchText) ||
      String(product.articul)?.toLowerCase().includes(searchText) ||
      product.brandName?.toLowerCase().includes(searchText);

    const matchesBrand =
      !selectedBrandId.value || product.brandId === selectedBrandId.value;

    const matchesCategory =
      !selectedCategoryId.value ||
      product.categoryId === selectedCategoryId.value;

    return matchesSearch && matchesBrand && matchesCategory;
  }),
);

const { currentPage, totalPages, paginatedItems: paginatedProducts } = usePagination(filteredProducts)

function openProduct(productId) {
  const normalizedId = Number(productId);
  if (!Number.isInteger(normalizedId) || normalizedId <= 0) return;

  router.push({
    name: "productDetails",
    params: { id: String(normalizedId) },
  });
}

function openCreateProduct() {
  router.push({ name: "adminProductNew" });
}

function editProduct(product) {
  const normalizedId = Number(product.id);
  if (!Number.isInteger(normalizedId) || normalizedId <= 0) return;

  router.push({
    name: "adminProductDetails",
    params: { id: String(normalizedId) },
  });
}

function closeFormDialog() {
  if (submitting.value) return;
  formVisible.value = false;
  selectedProduct.value = null;
}

async function handleSubmitProduct(formData) {
  try {
    submitting.value = true;

    const payload = {
      articul: formData.articul?.trim(),
      basePrice: Number(formData.basePrice ?? 0),
      salePrice:
        formData.salePrice === null || formData.salePrice === ""
          ? null
          : Number(formData.salePrice),
      saleIsActive: !!formData.saleIsActive,
      quantity: Number(formData.quantity ?? 0),
      productTypeId: Number(
        formData.productTypeId ?? selectedProduct.value?.productTypeId,
      ),
      images: (formData.images ?? []).map((img) => ({
        id: img.id ?? null,
        url: (img.storageKey || img.url)?.trim() ?? "",
        isDefault: img.isDefault,
      })),
      characteristicValues: (
        formData.characteristicValues ??
        formData.variants ??
        []
      )
        .filter((v) => v.valueId)
        .map((v) => ({ valueId: Number(v.valueId) })),
    };

    if (!payload.articul) {
      toast.error("Артикул обязателен");
      return;
    }

    if (!payload.productTypeId) {
      toast.error("Выберите основной товар");
      return;
    }

    if (!payload.images.length && selectedProduct.value?.isDefault) {
      toast.error("Добавьте хотя бы одно изображение");
      return;
    }

    if (formMode.value === "create") {
      await createProduct(payload);
      toast.success("Товар создан");
    } else if (formMode.value === "edit" && selectedProduct.value?.id) {
      payload.id = selectedProduct.value.id;
      await updateProduct(selectedProduct.value.id, payload);
      toast.success("Товар обновлён");
    }

    submitting.value = false;
    closeFormDialog();
    await loadProducts();
  } catch (error) {
    toast.error(
      error.response?.data?.message ?? "Ошибка при сохранении товара",
    );
  } finally {
    submitting.value = false;
  }
}

function deleteProduct(product) {
  productToDelete.value = product;
  deleteDialogVisible.value = true;
}

function closeDeleteDialog() {
  if (deleting.value) return;
  deleteDialogVisible.value = false;
  productToDelete.value = null;
}

async function confirmDeleteProduct(product) {
  if (deleting.value) return
  try {
    deleting.value = true;

    await deleteProductApi(product.id);
    toast.success("Товар удалён");

    await loadProducts();
  } catch (error) {
    toast.error("Не удалось удалить товар");
  } finally {
    deleting.value = false
    closeDeleteDialog();
  }
}

function goBack() {
  router.push("/admin");
}

onMounted(async () => {
  await Promise.all([loadProducts(), loadProductTypes()]);
});
</script>

<template>
  <div class="admin-products-page">
    <div class="page-shell">
      <div class="top-bar">
        <Button
          label="Назад"
          icon="pi pi-arrow-left"
          class="back-btn"
          outlined
          @click="goBack"
        />
        <Button
          label="Добавить товар"
          icon="pi pi-plus"
          class="create-btn"
          @click="openCreateProduct"
        />
      </div>

      <div class="page-header">
        <div>
          <h1>Товары</h1>
          <p>Управление товарами, быстрый поиск и редактирование каталога</p>
        </div>
      </div>

      <Card class="products-card">
        <template #content>
          <div class="filters">
            <div class="filter-group filter-group--search">
              <label>Поиск</label>
              <InputText
                v-model="search"
                placeholder="Поиск по названию, артикулу или бренду"
                class="filter-control search-control"
              />
            </div>

            <div class="filter-group">
              <label>Бренд</label>
              <Dropdown
                v-model="selectedBrandId"
                :options="brandOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Все бренды"
                class="filter-control"
              />
            </div>

            <div class="filter-group">
              <label>Категория</label>
              <Dropdown
                v-model="selectedCategoryId"
                :options="categoryOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Все категории"
                class="filter-control"
              />
            </div>
          </div>

          <div v-if="loading" class="state-block">Загрузка...</div>

          <div v-else-if="filteredProducts.length === 0" class="state-block">
            Товары не найдены
          </div>

          <div v-else class="products-list">
            <div
              v-for="product in paginatedProducts"
              :key="product.id"
              class="product-item"
            >
              <div class="product-left">
                <div class="product-image-wrap">
                  <img
                    v-if="product.defaultImageUrl"
                    :src="product.defaultImageUrl"
                    :alt="product.name"
                    class="product-image"
                  />
                  <div v-else class="image-placeholder">Нет изображения</div>
                </div>

                <div class="product-info">
                  <div class="product-name">
                    {{ product.name }}
                  </div>

                  <div class="product-meta">
                    <span>{{ product.brandName }}</span>
                    <span>{{ product.categoryName }}</span>
                    <span>Артикул: {{ product.articul }}</span>
                    <span>Остаток: {{ product.quantity }}</span>
                  </div>

                  <div class="product-price">
                    <span
                      v-if="product.saleIsActive && product.salePriceKzt != null"
                      class="old-price"
                    >
                      {{ product.basePriceKzt }} ₸
                    </span>

                    <span class="actual-price">
                      {{
                        product.saleIsActive && product.salePriceKzt != null
                        ? product.salePriceKzt
                        : product.basePriceKzt
                      }}
                      ₸
                  </span>

                  <span class="price-eur">
                    {{
                      product.saleIsActive && product.salePrice != null
                      ? product.salePrice
                      : product.basePrice
                    }}
                    €
                  </span>
                  </div>
                </div>
              </div>

              <div class="product-actions">
                <Button
                  icon="pi pi-eye"
                  class="icon-btn"
                  outlined
                  @click="openProduct(product.id)"
                />
                <Button
                  icon="pi pi-pencil"
                  class="icon-btn"
                  outlined
                  @click="editProduct(product)"
                />
                <Button
                  icon="pi pi-trash"
                  class="icon-btn icon-btn--danger"
                  outlined
                  @click="deleteProduct(product)"
                />
              </div>
            </div>
          </div>
          <div v-if="totalPages > 1" class="pagination">
            <Button icon="pi pi-chevron-left" outlined :disabled="currentPage === 1" @click="currentPage--" />
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <Button icon="pi pi-chevron-right" outlined :disabled="currentPage === totalPages" @click="currentPage++" />
          </div>
        </template>
      </Card>

      <ProductFormDialog
        :visible="formVisible"
        :mode="formMode"
        :product="selectedProduct"
        :product-types="productTypes"
        :submitting="submitting"
        @close="closeFormDialog"
        @submit="handleSubmitProduct"
      />

      <DeleteProductConfirmDialog
        :visible="deleteDialogVisible"
        :product="productToDelete"
        :deleting="deleting"
        @close="closeDeleteDialog"
        @confirm="confirmDeleteProduct"
      />
    </div>
  </div>
</template>

<style scoped>
.admin-products-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-shell {
  max-width: 1280px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.products-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

:deep(.products-card.p-card) {
  background: #ffffff;
}

:deep(.products-card .p-card-content) {
  padding: 0;
}

.filters {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid #efefef;
  background: #ffffff;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group label {
  font-size: 13px;
  font-weight: 600;
  color: #444444;
}

.filter-control {
  width: 100%;
}

.search-control {
  min-width: 0;
}

:deep(.p-inputtext),
:deep(.p-dropdown) {
  width: 100%;
  border-radius: 16px;
  border-color: #dddddd;
  box-shadow: none;
}

:deep(.p-inputtext:enabled:focus),
:deep(.p-dropdown:not(.p-disabled).p-focus) {
  border-color: #111111;
  box-shadow: 0 0 0 3px rgba(196, 18, 23, 0.08);
}

.state-block {
  margin: 24px;
  padding: 28px;
  text-align: center;
  color: #777777;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  background: #fafafa;
}

.products-list {
  display: flex;
  flex-direction: column;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 20px 0 8px;
}

.page-info {
  font-size: 14px;
  color: #555;
  min-width: 60px;
  text-align: center;
}

.product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  transition: background 0.2s ease;
}

.product-item:first-child {
  border-top: none;
}

.product-item:hover {
  background: #fcfcfc;
}

.product-left {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}

.product-image-wrap {
  width: 96px;
  height: 96px;
  background: #f3f4f6;
  overflow: hidden;
  flex-shrink: 0;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7b7b7b;
  font-size: 12px;
  text-align: center;
  padding: 8px;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.product-name {
  font-size: 20px;
  font-weight: 600;
  color: #111111;
  word-break: break-word;
  letter-spacing: -0.02em;
}

.product-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 13px;
  color: #6f6f6f;
}

.product-price {
  display: flex;
  align-items: center;
  gap: 10px;
}

.old-price {
  text-decoration: line-through;
  color: #9ca3af;
}

.actual-price {
  font-weight: 700;
  font-size: 18px;
  color: #111111;
}

.product-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.create-btn.p-button) {
  background: #111111;
  border: 1px solid #111111;
  color: #ffffff;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.create-btn.p-button:hover) {
  background: #2a2a2a;
  border-color: #2a2a2a;
}

:deep(.icon-btn.p-button) {
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #111111;
  border-radius: 999px;
  width: 42px;
  height: 42px;
  box-shadow: none;
}

:deep(.icon-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.icon-btn.p-button .p-button-icon) {
  font-size: 14px;
}

:deep(.icon-btn--danger.p-button:hover) {
  border-color: #c41217;
  color: #c41217;
}

@media (max-width: 980px) {
  .filters {
    grid-template-columns: 1fr;
  }

  .product-item {
    flex-direction: column;
    align-items: stretch;
  }

  .product-left {
    align-items: flex-start;
  }

  .product-actions {
    justify-content: flex-end;
  }
}

@media (max-width: 640px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .product-left {
    flex-direction: column;
  }

  .product-image-wrap {
    width: 100%;
    height: 220px;
  }

  .product-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .page-header h1 {
    font-size: 30px;
  }
}
.price-eur {
  font-size: 13px;
  color: #6b7280;
}
</style>
